import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Camera, Check, CheckCircle2, ChevronDown, ChevronRight, ChevronUp, Clock, Droplet,
  Loader2, Lock, LogOut, Moon, PauseCircle, QrCode, Repeat, Sun, Truck, Warehouse, WifiOff, XCircle,
} from 'lucide-react';
import {
  mdiAccountCircle, mdiCalendarClock, mdiCashPlus, mdiCellphoneArrowDown, mdiClipboardCheckOutline, mdiHelpCircleOutline,
  mdiHistory, mdiLockReset, mdiPail, mdiPencilCircleOutline, mdiPhone, mdiTranslate, mdiTruckDelivery, mdiWaterAlert,
} from '@mdi/js';
import { MdiIcon } from './MdiIcon';
import { LANGS, LangProvider, useLang, type WordKey } from './i18n';
import { useAuth } from '../hooks/useAuth';
import { getApiErrorMessage } from '../services/api';
import { useFormRule } from '../features/common/formSettings';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { formatCurrency, formatDate } from '../utils/format';
import { cn } from '../utils/cn';
import { logoUrl, telHref, useSite } from '../utils/site';
import { QrScanner } from './QrScanner';
import {
  addDays, agentApi, farmShift, getPosition, MAX_OWN_CORRECTIONS, todayIso, type AgentDetail, type Delivery, type HerdAnimal, type MilkEntry, type MilkShift, type MyTask, type RouteItem, type Shift,
} from './agentApi';

// The delivery agent's page on the website (/agent) - everything the
// agent does in the mobile app, in the phone's browser, so a link is enough
// (no app to install). Same staff login and the same server endpoints as
// the app, so the office sees each delivery the moment it's saved. The
// list refreshes by itself every 30 seconds.

const LIVE_MS = 30_000;
const keyOf = (r: RouteItem) => `${r.customer_id}-${r.sale_date}-${r.shift}`;
const time = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
const MODE: Record<string, string> = { QRScan: 'QR scanned', OfflineBypass: 'No signal (GPS)', Skipped: 'Skipped' };

// The same icons as the mobile app (Material Design). History, missed /
// wrong entries, language and help live under Profile.
type Tab = 'deliveries' | 'milking' | 'sale' | 'tasks' | 'spill' | 'profile';
const TABS: { id: Tab; word: WordKey; icon: string }[] = [
  { id: 'deliveries', word: 'deliveries', icon: mdiTruckDelivery },
  { id: 'milking', word: 'milking', icon: mdiPail },
  // Dairy Hub, Partner, Customer, Vendor, Calves, Office - as the app's Milk Sale.
  { id: 'sale', word: 'milkDelivery', icon: mdiCashPlus },
  { id: 'tasks', word: 'tasks', icon: mdiClipboardCheckOutline },
  { id: 'spill', word: 'spillage', icon: mdiWaterAlert },
  { id: 'profile', word: 'profile', icon: mdiAccountCircle },
];

function ShiftBadge({ shift }: { shift?: string | null }) {
  if (!shift) return null;
  const am = shift === 'Morning';
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-bold', am ? 'bg-warning-50 text-brand-gold-700' : 'bg-brand-green-900 text-white')}>
      {am ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />} {am ? 'AM' : 'PM'}
    </span>
  );
}

function Notice({ tone, children }: { tone: 'ok' | 'warn' | 'bad'; children: React.ReactNode }) {
  return (
    <p className={cn('rounded-lg p-3 text-sm font-medium',
      tone === 'ok' ? 'bg-success-50 text-success-700' : tone === 'warn' ? 'bg-warning-50 text-warning-700' : 'bg-critical-50 text-critical-700')}>
      {children}
    </p>
  );
}

/** The shift by the time now - follows the clock past 12:00 while the page is open. */
function useFarmShift(): Shift {
  const [s, setS] = useState<Shift>(() => farmShift());
  useEffect(() => {
    const t = setInterval(() => setS(farmShift()), 30_000);
    return () => clearInterval(t);
  }, []);
  return s;
}

/** AM / PM (/ 3rd) buttons: set by the time unless the agent picks another; "Auto" goes back. */
function ShiftPicker<T extends string>({ options, value, auto, manual, onPick }: {
  options: { id: T; label: string }[]; value: T; auto: T; manual: boolean; onPick: (s: T | null) => void;
}) {
  const { t } = useLang();
  return (
    <div className="flex flex-col gap-1">
      <div className={cn('grid gap-2', options.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
        {options.map((s) => (
          <button key={s.id} type="button" onClick={() => onPick(s.id === auto ? null : s.id)}
            className={cn('rounded-xl border py-2 text-sm font-bold', value === s.id ? 'border-brand-green-900 bg-brand-green-900 text-white' : 'border-ink-300/50 text-ink-700')}>
            {s.label}
          </button>
        ))}
      </div>
      <p className="flex items-center gap-2 text-xs text-ink-500">
        {manual ? (
          <>
            <span className="font-semibold text-warning-700">{t('changedByHand')}</span>
            <button type="button" onClick={() => onPick(null)} className="font-bold text-brand-green-800 underline">{t('autoByTime')}</button>
          </>
        ) : <span>{t('setByTime')}</span>}
      </p>
    </div>
  );
}

const MAX_DAYS_BACK = 30;

/** Yesterday / an earlier day - Profile -> Record a missed session. */
function DayPicker({ value, onChange }: { value: string; onChange: (d: string) => void }) {
  const { t } = useLang();
  const today = todayIso();
  const yesterday = addDays(today, -1);
  const [otherOpen, setOtherOpen] = useState(value !== yesterday);
  const chip = (on: boolean) => cn('rounded-xl border py-2 text-sm font-bold', on ? 'border-brand-green-900 bg-brand-green-900 text-white' : 'border-ink-300/50 text-ink-700');
  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className={chip(value === yesterday && !otherOpen)} onClick={() => { setOtherOpen(false); onChange(yesterday); }}>{t('yesterday')}</button>
        <button type="button" className={chip(otherOpen)} onClick={() => setOtherOpen(true)}>{t('otherDay')}</button>
      </div>
      {otherOpen && (
        <input type="date" className={inputClass} min={addDays(today, -MAX_DAYS_BACK)} max={addDays(today, -1)} value={value}
          onChange={(e) => e.target.value && onChange(e.target.value)} />
      )}
    </div>
  );
}

/** Shown on Milking / Milk Delivery while recording for a missed day (started from Profile). */
function MissedBanner({ day, onToday }: { day: string; onToday: () => void }) {
  const { t } = useLang();
  if (day === todayIso()) return null;
  return (
    <div className="flex items-center gap-2 rounded-xl border border-warning-500/40 bg-warning-50 p-3 text-sm text-warning-700">
      <MdiIcon path={mdiCalendarClock} className="h-5 w-5 shrink-0" />
      <span className="flex-1 font-semibold">Missed entry for {formatDate(day)} - pick the shift it was for. The office checks it before it counts.</span>
      <button type="button" onClick={onToday} className="shrink-0 rounded-lg bg-white px-2.5 py-1.5 text-xs font-bold text-brand-green-800">{t('backToToday')}</button>
    </div>
  );
}

const inputClass = 'w-full rounded-lg border border-ink-300/50 bg-white px-3 py-2.5 text-base focus:border-brand-green-600 focus:outline-none';
const bigBtn = 'inline-flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold disabled:opacity-50';

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------

function AgentLogin() {
  const { login, isLoading } = useAuth();
  const site = useSite();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in.');
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-subtle p-4">
      <form onSubmit={submit} className="flex w-full max-w-sm flex-col gap-4 rounded-2xl bg-white p-6 shadow-card">
        <div className="flex flex-col items-center gap-2 text-center">
          <img src={logoUrl(site)} alt="" className="h-16 w-16 object-contain" />
          <h1 className="text-xl font-bold text-brand-green-900">Delivery Agent</h1>
          <p className="text-sm text-ink-500">Sri KalpaGavya · sign in with your staff username and password</p>
        </div>
        <label className="flex flex-col gap-1 text-sm font-semibold text-ink-700">Username
          <input className={inputClass} autoComplete="username" autoCapitalize="none" value={username} onChange={(e) => setUsername(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm font-semibold text-ink-700">Password
          <input className={inputClass} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error && <Notice tone="bad">{error}</Notice>}
        <button type="submit" disabled={!username.trim() || !password || isLoading} className={cn(bigBtn, 'bg-brand-green-700 text-base text-white')}>
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Sign in
        </button>
        <p className="text-center text-xs text-ink-400">Tip: browser menu → "Add to Home screen" to open this like an app.</p>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deliveries
// ---------------------------------------------------------------------------

function DayChangeSheet({ row, plan, onClose, onDone }: { row: RouteItem; plan: RouteItem[]; onClose: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<'move' | 'pause' | 'quantity'>('move');
  const [days, setDays] = useState(1);
  const am = plan.find((p) => p.shift === 'Morning');
  const pm = plan.find((p) => p.shift === 'Evening');
  const [amL, setAmL] = useState(am ? String(Number(am.litres)) : '0');
  const [pmL, setPmL] = useState(pm ? String(Number(pm.litres)) : '0');
  const save = useMutation({
    mutationFn: (c: Parameters<typeof agentApi.dayChange>[1]) => agentApi.dayChange(row.customer_id, c),
    onSuccess: () => { onDone(); onClose(); },
  });
  const only = plan.length === 1 ? plan[0].shift : null;
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-md flex-col gap-3 overflow-y-auto rounded-t-2xl bg-white p-4 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-bold">{row.customer_name} · {formatDate(row.sale_date)}</p>
          <button type="button" onClick={onClose} aria-label="Close"><XCircle className="h-6 w-6 text-ink-400" /></button>
        </div>
        <p className="text-sm text-ink-500">Usual: {plan.map((p) => `${p.shift === 'Morning' ? 'AM' : 'PM'} ${Number(p.litres)} L`).join(' + ')}</p>
        <div className="grid grid-cols-3 gap-2">
          {([['move', 'AM ↔ PM', Repeat], ['pause', 'Pause', PauseCircle], ['quantity', 'More / less', Droplet]] as const).map(([m, label, Icon]) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={cn('flex flex-col items-center gap-1 rounded-xl border py-2.5 text-xs font-bold', mode === m ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/50 text-ink-700')}>
              <Icon className="h-5 w-5" /> {label}
            </button>
          ))}
        </div>
        {mode === 'move' && (
          <div className="grid grid-cols-2 gap-2">
            {(['Morning', 'Evening'] as Shift[]).map((s) => (
              <button key={s} type="button" disabled={only === s || save.isPending} onClick={() => save.mutate({ kind: 'move', start_date: row.sale_date, to_shift: s })}
                className={cn(bigBtn, 'bg-brand-green-900 text-white')}>{s === 'Morning' ? 'All in AM' : 'All in PM'}</button>
            ))}
          </div>
        )}
        {mode === 'pause' && (
          <>
            <div className="flex items-center justify-center gap-4">
              <button type="button" className="h-11 w-11 rounded-full border text-xl" onClick={() => setDays((n) => Math.max(1, n - 1))}>−</button>
              <span className="min-w-[6rem] text-center text-lg font-bold">{days} day{days === 1 ? '' : 's'}</span>
              <button type="button" className="h-11 w-11 rounded-full border text-xl" onClick={() => setDays((n) => Math.min(60, n + 1))}>+</button>
            </div>
            <p className="text-center text-xs text-ink-500">No milk and no charge until {formatDate(addDays(row.sale_date, days - 1))}</p>
            <button type="button" disabled={save.isPending} onClick={() => save.mutate({ kind: 'pause', start_date: row.sale_date, end_date: addDays(row.sale_date, days - 1) })}
              className={cn(bigBtn, 'bg-brand-green-700 text-white')}>Pause</button>
          </>
        )}
        {mode === 'quantity' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-sm font-semibold">AM litres<input className={inputClass} inputMode="decimal" value={amL} onChange={(e) => setAmL(e.target.value)} /></label>
              <label className="text-sm font-semibold">PM litres<input className={inputClass} inputMode="decimal" value={pmL} onChange={(e) => setPmL(e.target.value)} /></label>
            </div>
            <p className="text-xs text-ink-500">0 = no milk in that shift. Only this day changes.</p>
            <button type="button" disabled={save.isPending || !(Number(amL) >= 0 && Number(pmL) >= 0)}
              onClick={() => save.mutate({ kind: 'quantity', start_date: row.sale_date, morning_litres: Number(amL || 0), evening_litres: Number(pmL || 0) })}
              className={cn(bigBtn, 'bg-brand-green-700 text-white')}>Save</button>
          </>
        )}
        {save.isError && <Notice tone="bad">{getApiErrorMessage(save.error, 'Could not change this delivery.')}</Notice>}
      </div>
    </div>
  );
}

function DeliveriesTab({ day, refresh, isAdmin, goHub }: { day: AgentDetail; refresh: () => void; isAdmin: boolean; goHub: () => void }) {
  const { t } = useLang();
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [scanFor, setScanFor] = useState<RouteItem | null>(null);
  const [changeFor, setChangeFor] = useState<RouteItem | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null);
  const iso = todayIso();
  const rows = day.pending;
  // Later = today's PM before 12:00; today's AM after 12:00 is missed, not later.
  const isLater = (r: RouteItem) => !r.is_current_shift && r.sale_date === iso && r.shift === 'Evening';
  const now = rows.filter((r) => r.is_current_shift);
  const later = rows.filter(isLater);
  const earlier = rows.filter((r) => !r.is_current_shift && !isLater(r));
  const hubDue = (day.hub ?? []).filter((h) => h.is_current_shift && h.sale_count === 0).length;

  const run = async (r: RouteItem, work: () => Promise<Delivery | unknown>, ok: (d: Delivery) => { tone: 'ok' | 'warn'; text: string }) => {
    setBusy(keyOf(r));
    setMsg(null);
    try {
      const d = (await work()) as Delivery;
      setMsg(ok(d));
      refresh();
    } catch (err) {
      setMsg({ tone: 'bad', text: getApiErrorMessage(err, 'Could not save this delivery.') });
    } finally {
      setBusy(null);
    }
  };

  const scanned = (r: RouteItem, token: string) => {
    setScanFor(null);
    run(r, () => agentApi.confirm({ customer_id: r.customer_id, sale_date: r.sale_date, verification_mode: 'QRScan', qr_token: token.trim() }),
      (d) => ({ tone: d.account_warning ? 'warn' : 'ok', text: `✓ ${r.customer_name}: ${Number(d.litres ?? r.litres)} L delivered.${d.account_warning ? ` ${d.account_warning}` : ''}` }));
  };

  const noSignal = async (r: RouteItem) => {
    if (!window.confirm(`${r.customer_name}: record the ${r.shift === 'Morning' ? 'AM' : 'PM'} delivery without the QR? It counts once the office approves it.`)) return;
    setBusy(keyOf(r));
    const pos = await getPosition();
    if (!pos) {
      setBusy(null);
      setMsg({ tone: 'bad', text: 'Turn on location (GPS) and allow it for this site - a delivery without QR needs your location.' });
      return;
    }
    run(r, () => agentApi.confirm({
      customer_id: r.customer_id, sale_date: r.sale_date, verification_mode: 'OfflineBypass',
      captured_latitude: pos.coords.latitude, captured_longitude: pos.coords.longitude, captured_at: new Date().toISOString(),
    }), (d) => ({ tone: 'warn', text: `⏳ ${r.customer_name}: saved without QR - waiting for the office to approve.${d.account_warning ? ` ${d.account_warning}` : ''}` }));
  };

  const skip = (r: RouteItem) => {
    if (!window.confirm(`No delivery for ${r.customer_name} (${r.shift === 'Morning' ? 'AM' : 'PM'}, ${formatDate(r.sale_date)})?`)) return;
    run(r, () => agentApi.skip(r.customer_id, r.sale_date, r.shift), () => ({ tone: 'ok', text: `${r.customer_name}: skipped.` }));
  };

  const card = (r: RouteItem) => {
    const k = keyOf(r);
    const expanded = open === k;
    const amount = Number(r.litres) * Number(r.rate_per_litre);
    return (
      <li key={k} className={cn('rounded-xl border bg-white p-3', r.is_current_shift ? 'border-l-4 border-l-info-600 border-ink-300/30' : 'border-ink-300/30')}>
        <button type="button" onClick={() => setOpen(expanded ? null : k)} className="flex w-full items-center gap-2 text-left">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-bold">{r.customer_name}</span>
            <span className="block text-xs text-ink-500">
              {formatDate(r.sale_date)}{isAdmin ? ` · ${r.assigned_agent_name ?? 'Unassigned'}` : ''}
            </span>
            {r.change_note && <span className="mt-1 inline-block rounded-full bg-warning-50 px-2 py-0.5 text-xs font-bold text-warning-700">{r.change_note}</span>}
          </span>
          <ShiftBadge shift={r.shift} />
          {expanded ? <ChevronUp className="h-5 w-5 text-ink-400" /> : <ChevronDown className="h-5 w-5 text-ink-400" />}
        </button>
        <div className="mt-2 flex items-center gap-4 text-lg font-bold">
          <span>{Number(r.litres)} L</span>
          <span className="text-ink-600">{formatCurrency(amount)}</span>
        </div>
        {expanded && (
          <dl className="mt-2 grid grid-cols-[6rem_1fr] gap-y-1 border-t border-ink-300/20 pt-2 text-sm">
            <dt className="text-ink-500">Rate</dt><dd>₹{Number(r.rate_per_litre)} / L</dd>
            <dt className="text-ink-500">Billing</dt><dd>{r.billing_mode === 'Prepaid' ? `Prepaid wallet${r.wallet_balance != null ? ` · ${formatCurrency(r.wallet_balance)}` : ''}` : 'Pay later'}</dd>
            {Number(r.overdue_amount ?? 0) > 0 && <><dt className="text-ink-500">Overdue</dt><dd className="text-critical-700">{formatCurrency(r.overdue_amount)}</dd></>}
            {isLater(r) && <><dt className="text-ink-500">Note</dt><dd>PM delivery - opens at 12:00</dd></>}
            {!r.is_current_shift && !isLater(r) && <><dt className="text-ink-500">Note</dt><dd>Past shift - the QR only works for the shift on now</dd></>}
          </dl>
        )}
        {r.account_warning && (
          <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-warning-50 p-2 text-sm font-semibold text-warning-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {r.account_warning}</p>
        )}
        {busy === k ? <Loader2 className="mx-auto mt-3 h-6 w-6 animate-spin text-brand-green-700" /> : (
          <div className="mt-3 grid grid-cols-4 gap-2">
            {r.is_current_shift && (
              <button type="button" onClick={() => setScanFor(r)} className="col-span-2 flex flex-col items-center gap-0.5 rounded-xl bg-brand-green-700 py-2.5 text-xs font-bold text-white">
                <QrCode className="h-6 w-6" /> {t('scanQr')}
              </button>
            )}
            {!isLater(r) && (
              <button type="button" onClick={() => noSignal(r)} className="flex flex-col items-center gap-0.5 rounded-xl border border-brand-green-700 py-2.5 text-center text-xs font-bold text-brand-green-800">
                <WifiOff className="h-5 w-5" /> {t('noQr', { short: true })}
              </button>
            )}
            <button type="button" onClick={() => skip(r)} className="flex flex-col items-center gap-0.5 rounded-xl border border-brand-green-700 py-2.5 text-center text-xs font-bold text-brand-green-800">
              <XCircle className="h-5 w-5" /> {t('skip', { short: true })}
            </button>
            {r.sale_date === iso && (
              <button type="button" onClick={() => setChangeFor(r)} className="flex flex-col items-center gap-0.5 rounded-xl border border-brand-green-700 py-2.5 text-center text-xs font-bold text-brand-green-800">
                <Repeat className="h-5 w-5" /> {t('change', { short: true })}
              </button>
            )}
          </div>
        )}
      </li>
    );
  };

  const section = (title: string, list: RouteItem[], Icon: typeof Clock) => !!list.length && (
    <section className="flex flex-col gap-2">
      <h2 className="mt-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink-600"><Icon className="h-4 w-4" /> {title} ({list.length})</h2>
      <ul className="flex flex-col gap-2">{list.map(card)}</ul>
    </section>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 rounded-xl bg-white p-4">
        <Truck className="h-9 w-9 text-info-600" />
        <div>
          <p className="text-3xl font-extrabold leading-none">{now.length + hubDue}</p>
          <p className="text-sm text-ink-500">{t('toDeliverNow')}{later.length ? ` · ${later.length} ${t('later', { short: true })}` : ''}{earlier.length ? ` · ${earlier.length} ${t('earlier', { short: true })}` : ''} · {day.done_today.length} {t('doneWord', { short: true })}</p>
        </div>
      </div>
      {hubDue > 0 && (
        <button type="button" onClick={goHub} className="flex items-center gap-2 rounded-xl bg-info-50 p-3 text-left text-sm font-semibold text-info-700">
          <Warehouse className="h-5 w-5" /> Dairy Hub milk for this shift is still to record → tap here
        </button>
      )}
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {section(t('deliverNow'), now, Truck)}
      {section(t('laterToday'), later, Clock)}
      {section(t('earlierNotDone'), earlier, AlertTriangle)}
      {!rows.length && (
        <div className="flex flex-col items-center gap-2 rounded-xl bg-white p-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-success-600" />
          <p className="font-bold">{t('allDone')}</p>
        </div>
      )}
      {!!day.done_today.length && (
        <section className="flex flex-col gap-2">
          <h2 className="mt-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink-600"><CheckCircle2 className="h-4 w-4" /> {t('doneToday')} ({day.done_today.length})</h2>
          <ul className="flex flex-col gap-1.5">{day.done_today.map((d) => <DoneRow key={d.id} d={d} />)}</ul>
        </section>
      )}
      {scanFor && <QrScanner title={`${scanFor.customer_name} · ${scanFor.shift === 'Morning' ? 'AM' : 'PM'}`} onClose={() => setScanFor(null)} onResult={(code) => scanned(scanFor, code)} />}
      {changeFor && (
        <DayChangeSheet row={changeFor} plan={rows.filter((p) => p.customer_id === changeFor.customer_id && p.sale_date === changeFor.sale_date)}
          onClose={() => setChangeFor(null)} onDone={() => { setMsg({ tone: 'ok', text: `${changeFor.customer_name}: delivery changed.` }); refresh(); }} />
      )}
    </div>
  );
}

function DoneRow({ d }: { d: Delivery }) {
  const skipped = d.verification_mode === 'Skipped';
  return (
    <li className={cn('flex items-center gap-2 rounded-xl border-l-4 bg-white px-3 py-2', skipped ? 'border-l-ink-300' : 'border-l-success-600')}>
      {skipped ? <XCircle className="h-5 w-5 shrink-0 text-ink-400" /> : <CheckCircle2 className="h-5 w-5 shrink-0 text-success-600" />}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{d.customer_name}</span>
        <span className="block text-xs text-ink-500">
          {MODE[d.verification_mode] ?? d.verification_mode} · {time(d.captured_at)}{d.litres != null ? ` · ${Number(d.litres)} L` : ''}
          {!skipped && d.sale_status === 'Pending' ? ' · waiting for office' : ''}
          {d.gps_flag === 'Suspicious' ? ` · ${Math.round(d.distance_from_registered_m ?? 0)} m from home` : ''}
        </span>
      </span>
      <ShiftBadge shift={d.shift} />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Record Milking - one cow, or a batch with one combined quantity (same as
// the app's Milk Session). Cows are picked from the list or scanned (QR or
// ear-tag barcode).
// ---------------------------------------------------------------------------

const MILKING_STATUSES = ['Lactating', 'PregnantLactating'];
const isMilking = (a: HerdAnimal) => MILKING_STATUSES.includes(a.production_status ?? '')
  && !/sold|dead|disposed|returned/i.test(a.health_status ?? '');
const cowName = (a: HerdAnimal) => `${a.animal_id}${a.animal_name ? ` · ${a.animal_name}` : ''}`;

/** A cow that's already done this shift: its entry, and either a correction
 * of one's own (twice, within 24 hours, while Pending) or a correction
 * request with the reason, for the office to decide. */
function EntrySheet({ entry, userId, cows, onClose, onDone }: {
  entry: MilkEntry; userId: string; cows: Map<string, HerdAnimal>; onClose: () => void; onDone: (text: string) => void;
}) {
  const mine = entry.entered_by === userId;
  const used = entry.agent_edit_count ?? 0;
  const ageMs = Date.now() - new Date(entry.created_at).getTime();
  const canFix = mine && entry.approval_status === 'Pending' && used < MAX_OWN_CORRECTIONS && ageMs <= 24 * 3600_000;
  const whyNot = !mine ? 'Recorded by someone else.' : entry.approval_status !== 'Pending' ? `Already ${entry.approval_status.toLowerCase()} by the office.`
    : used >= MAX_OWN_CORRECTIONS ? `Already corrected ${MAX_OWN_CORRECTIONS} times.` : 'More than 24 hours since it was recorded.';
  const req = entry.correction_request;
  const [litres, setLitres] = useState(String(Number(req?.litres ?? entry.litres)));
  const [fat, setFat] = useState(entry.fat_percent != null ? String(Number(entry.fat_percent)) : '');
  const [snf, setSnf] = useState(entry.snf_percent != null ? String(Number(entry.snf_percent)) : '');
  const [remarks, setRemarks] = useState(entry.remarks ?? '');
  const [reason, setReason] = useState('');
  const figures = () => ({
    litres: Number(litres), fat_percent: fat ? Number(fat) : undefined, snf_percent: snf ? Number(snf) : undefined, remarks: remarks || undefined,
  });
  const save = useMutation({
    mutationFn: () => (canFix ? agentApi.myCorrection(entry.id, figures()) : agentApi.requestCorrection(entry.id, { ...figures(), reason: reason.trim() })),
    onSuccess: () => onDone(canFix
      ? `✓ ${entry.session_code ?? 'Entry'} corrected (${used + 1} of ${MAX_OWN_CORRECTIONS}).`
      : `⏳ Correction asked for ${entry.session_code ?? 'the entry'} - the office will approve or reject it.`),
  });
  const who = entry.animal_id ? (cows.get(entry.animal_id) ? cowName(cows.get(entry.animal_id)!) : 'Cow') : `Batch of ${entry.group_animal_ids?.length ?? 0} cows`;
  const valid = Number(litres) >= 0 && litres !== '' && (fat === '' || (Number(fat) >= 0 && Number(fat) <= 100)) && (canFix || reason.trim().length >= 5);

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-md flex-col gap-3 overflow-y-auto rounded-t-2xl bg-white p-4 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-bold">{who}</p>
            <p className="text-xs text-ink-500">{entry.session_code} · {entry.session_type === 'Morning' ? 'AM' : entry.session_type === 'Evening' ? 'PM' : '3rd'} · recorded {new Date(entry.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><XCircle className="h-6 w-6 text-ink-400" /></button>
        </div>
        <div className="grid grid-cols-3 gap-2 rounded-lg bg-surface-subtle p-3 text-center text-sm">
          <span><span className="block text-xs text-ink-500">Litres</span><b>{Number(entry.litres)}</b></span>
          <span><span className="block text-xs text-ink-500">Fat %</span><b>{entry.fat_percent != null ? Number(entry.fat_percent) : '—'}</b></span>
          <span><span className="block text-xs text-ink-500">Status</span><b className={entry.approval_status === 'Approved' ? 'text-success-700' : 'text-warning-700'}>{entry.approval_status}</b></span>
        </div>
        {req && (
          <Notice tone="warn">Correction already asked{req.requested_by_name ? ` by ${req.requested_by_name}` : ''}: {Number(req.litres)} L{req.fat_percent != null ? `, fat ${req.fat_percent}%` : ''} - "{req.reason}". Waiting for the office. Sending again replaces it.</Notice>
        )}
        {canFix ? (
          <Notice tone="warn">
            You can correct your own entry {MAX_OWN_CORRECTIONS} times within 24 hours of recording it, while the office hasn't approved it -
            {' '}{MAX_OWN_CORRECTIONS - used} correction{MAX_OWN_CORRECTIONS - used === 1 ? '' : 's'} left. After that, ask the office for a correction.
          </Notice>
        ) : (
          <Notice tone="bad">Can't change it directly: {whyNot} Send a correction request with the right figures and the reason - the office approves or rejects it.</Notice>
        )}
        <div className="grid grid-cols-3 gap-2">
          <label className="text-sm font-semibold">Litres *<input className={inputClass} inputMode="decimal" value={litres} onChange={(e) => setLitres(e.target.value)} /></label>
          <label className="text-sm font-semibold">Fat %<input className={inputClass} inputMode="decimal" value={fat} onChange={(e) => setFat(e.target.value)} /></label>
          <label className="text-sm font-semibold">SNF %<input className={inputClass} inputMode="decimal" value={snf} onChange={(e) => setSnf(e.target.value)} /></label>
        </div>
        <label className="text-sm font-semibold">Remarks<input className={inputClass} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></label>
        {!canFix && (
          <label className="text-sm font-semibold">Reason for the correction *
            <input className={inputClass} placeholder="e.g. Typed 11 instead of 13" value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
        )}
        {save.isError && <Notice tone="bad">{getApiErrorMessage(save.error, 'Could not save.')}</Notice>}
        <button type="button" disabled={!valid || save.isPending} onClick={() => save.mutate()} className={cn(bigBtn, 'bg-brand-green-700 text-base text-white')}>
          {save.isPending && <Loader2 className="h-5 w-5 animate-spin" />}
          {canFix ? `Save correction (${used + 1} of ${MAX_OWN_CORRECTIONS})` : 'Send correction request'}
        </button>
      </div>
    </div>
  );
}

function MilkingTab({ userId, day, onToday }: { userId: string; day: string; onToday: () => void }) {
  const qc = useQueryClient();
  const { t } = useLang();
  const milkShifts: { id: MilkShift; label: string }[] = [{ id: 'Morning', label: t('am') }, { id: 'Evening', label: t('pm') }, { id: 'Third', label: '3rd' }];
  const [openEntry, setOpenEntry] = useState<MilkEntry | null>(null);
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const auto = useFarmShift();
  const [manualShift, setManualShift] = useState<MilkShift | null>(null);
  const shift = manualShift ?? auto;
  const [picked, setPicked] = useState<string[]>([]);
  useEffect(() => { setPicked([]); setMsg(null); }, [day]);
  const [search, setSearch] = useState('');
  const [litres, setLitres] = useState('');
  const [fat, setFat] = useState('');
  const [snf, setSnf] = useState('');
  const [remarks, setRemarks] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null);
  const herd = useQuery({ queryKey: ['agent', 'herd'], queryFn: agentApi.herd, staleTime: 300_000 });
  const done = useQuery({ queryKey: ['agent', 'milk', day], queryFn: () => agentApi.milkOn(day), refetchInterval: LIVE_MS });
  // Only cows being milked now (Lactating, or Pregnant & Lactating) - dry,
  // sold, returned and other animals are left out of the list.
  const cows = useMemo(() => (herd.data ?? []).filter(isMilking), [herd.data]);
  // Each cow's entry for this day and shift (a rejected one doesn't count -
  // she can be recorded again). A cow with one can't be entered twice; tap
  // it to correct the entry or ask for a correction.
  const recorded = useMemo(() => {
    const m = new Map<string, MilkEntry>();
    (done.data ?? []).filter((e) => e.session_type === shift && e.approval_status !== 'Rejected').forEach((e) => {
      if (e.animal_id) m.set(e.animal_id, e);
      (e.group_animal_ids ?? []).forEach((id) => m.set(id, e));
    });
    return m;
  }, [done.data, shift]);
  const byId = useMemo(() => new Map(cows.map((a) => [a.id, a])), [cows]);
  const term = search.trim().toLowerCase();
  const shown = term ? cows.filter((a) => `${a.animal_id} ${a.animal_name ?? ''} ${a.primary_tag ?? ''}`.toLowerCase().includes(term)) : cows;

  const choose = (id: string) => {
    setMsg(null);
    const entry = recorded.get(id);
    if (entry) { setOpenEntry(entry); return; }
    if (mode === 'single') setPicked([id]);
    else setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };
  const scanned = async (code: string) => {
    setScanning(false);
    try {
      const a = await agentApi.lookupAnimal(code);
      if (!isMilking(a)) {
        setMsg({ tone: 'bad', text: `${cowName(a)} is not marked as milking (${a.production_status || a.health_status || 'no status'}). If she is being milked, ask the office to set her to Lactating.` });
        return;
      }
      const entry = recorded.get(a.id);
      if (entry) {
        setMsg({ tone: 'warn', text: `${cowName(a)} is already done for this shift - correct that entry below.` });
        setOpenEntry(entry);
        return;
      }
      if (mode === 'single') setPicked([a.id]);
      else setPicked((cur) => (cur.includes(a.id) ? cur : [...cur, a.id]));
      setMsg({ tone: 'ok', text: `✓ ${cowName(a)}` });
    } catch (err) {
      setMsg({ tone: 'bad', text: getApiErrorMessage(err, 'No animal found for that code.') });
    }
  };

  const save = useMutation({
    mutationFn: () => agentApi.milking({
      ...(mode === 'single' ? { animal_id: picked[0] } : { group_animal_ids: picked }),
      // A missed day gets a usual time for that shift (farm time).
      production_date: day, session_type: shift,
      milking_time: day === todayIso() ? new Date().toISOString() : `${day}T${shift === 'Morning' ? '06' : shift === 'Evening' ? '17' : '21'}:00:00+05:30`,
      litres: Number(litres), fat_percent: fat ? Number(fat) : undefined, snf_percent: snf ? Number(snf) : undefined,
      remarks: remarks || undefined,
    }, photo),
    onSuccess: () => {
      const who = mode === 'single' ? cowName(byId.get(picked[0])!) : `${picked.length} cows`;
      setMsg({ tone: 'ok', text: `✓ ${Number(litres)} L saved for ${who} (${day === todayIso() ? '' : `${formatDate(day)} `}${shift === 'Morning' ? 'AM' : shift === 'Evening' ? 'PM' : '3rd'}). The office approves it.` });
      setPicked([]); setLitres(''); setFat(''); setSnf(''); setRemarks(''); setPhoto(null);
      qc.invalidateQueries({ queryKey: ['agent', 'milk'] });
    },
  });
  const ready = (mode === 'single' ? picked.length === 1 : picked.length >= 2) && Number(litres) > 0
    && (fat === '' || (Number(fat) >= 0 && Number(fat) <= 100));
  const shiftTotal = (done.data ?? []).filter((m) => m.session_type === shift).reduce((t, m) => t + Number(m.litres), 0);

  return (
    <div className="flex flex-col gap-3">
      <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
        <MissedBanner day={day} onToday={onToday} />
        <p className="font-bold">{t('recordMilking')}</p>
        <div className="grid grid-cols-2 gap-2">
          {([['single', 'individual'], ['batch', 'batch']] as const).map(([m, word]) => (
            <button key={m} type="button" onClick={() => { setMode(m); setPicked([]); setMsg(null); }}
              className={cn('flex flex-col items-center rounded-xl border px-2 py-2.5 text-center text-sm font-bold', mode === m ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/50 text-ink-700')}>
              {t(word)}
            </button>
          ))}
        </div>
        <ShiftPicker options={milkShifts} value={shift} auto={auto as MilkShift} manual={manualShift !== null} onPick={setManualShift} />
        <p className="text-xs text-ink-500">{day === todayIso() ? 'This shift so far' : `${formatDate(day)}, this shift`}: {shiftTotal.toFixed(1)} L from {recorded.size} cow{recorded.size === 1 ? '' : 's'}.</p>
      </section>

      <section className="flex flex-col gap-2 rounded-xl bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold">{mode === 'single' ? t('whichCow') : `${t('whichCows')} (${picked.length})`}</p>
          <button type="button" onClick={() => setScanning(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-green-700 px-3 py-2 text-xs font-bold text-white">
            <QrCode className="h-4 w-4" /> {t('scanCode', { short: true })}
          </button>
        </div>
        <input className={inputClass} placeholder={t('searchCow', { short: true })} value={search} onChange={(e) => setSearch(e.target.value)} />
        {herd.isError && <Notice tone="bad">{getApiErrorMessage(herd.error, 'Could not load the cows.')}</Notice>}
        <ul className="flex max-h-72 flex-col divide-y divide-ink-300/20 overflow-y-auto rounded-lg border border-ink-300/30">
          {shown.map((a) => {
            const on = picked.includes(a.id);
            return (
              <li key={a.id}>
                <button type="button" onClick={() => choose(a.id)} className={cn('flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm', on && 'bg-brand-green-50')}>
                  <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center border', mode === 'single' ? 'rounded-full' : 'rounded', on ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300')}>
                    {on && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{cowName(a)}</span>
                    <span className="block text-xs text-ink-500">{[a.primary_tag, a.production_status].filter(Boolean).join(' · ')}</span>
                  </span>
                  {recorded.has(a.id) && (() => {
                    const e = recorded.get(a.id)!;
                    return e.correction_request
                      ? <span className="shrink-0 rounded-full bg-warning-50 px-2 py-0.5 text-[11px] font-bold text-warning-700">correction asked</span>
                      : <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold', e.approval_status === 'Approved' ? 'bg-success-50 text-success-700' : 'bg-info-50 text-info-700')}>
                          {e.approval_status === 'Approved' ? 'done ✓' : 'done · edit'}
                        </span>;
                  })()}
                </button>
              </li>
            );
          })}
          {herd.isSuccess && !shown.length && <li className="px-3 py-3 text-sm text-ink-500">No cow matches.</li>}
        </ul>
      </section>

      <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
        <div className="grid grid-cols-3 gap-2">
          <label className="text-sm font-semibold">{t('litres', { short: true })}{mode === 'batch' ? ' (total)' : ''} *<input className={inputClass} inputMode="decimal" value={litres} onChange={(e) => setLitres(e.target.value)} /></label>
          <label className="text-sm font-semibold">{t('fat', { short: true })}<input className={inputClass} inputMode="decimal" value={fat} onChange={(e) => setFat(e.target.value)} /></label>
          <label className="text-sm font-semibold">SNF %<input className={inputClass} inputMode="decimal" value={snf} onChange={(e) => setSnf(e.target.value)} /></label>
        </div>
        <label className="text-sm font-semibold">{t('remarks', { short: true })}<input className={inputClass} placeholder="e.g. 1 L spilled" value={remarks} onChange={(e) => setRemarks(e.target.value)} /></label>
        <label className={cn('flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-bold', photo ? 'border-success-600 text-success-700' : 'border-ink-300 text-ink-600')}>
          <Camera className="h-5 w-5" /> {photo ? 'Photo added ✓ (tap to retake)' : t('photoOptional')}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
        </label>
        {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
        {save.isError && <Notice tone="bad">{getApiErrorMessage(save.error, 'Could not save. Please try again.')}</Notice>}
        <button type="button" disabled={!ready || save.isPending} onClick={() => save.mutate()} className={cn(bigBtn, 'bg-brand-green-700 text-base text-white')}>
          {save.isPending && <Loader2 className="h-5 w-5 animate-spin" />} {t('saveMilking')}
        </button>
        {mode === 'batch' && picked.length === 1 && <p className="text-center text-xs text-ink-500">Pick at least 2 cows for a batch, or use Individual Milking.</p>}
      </section>
      {openEntry && (
        <EntrySheet entry={openEntry} userId={userId} cows={byId} onClose={() => setOpenEntry(null)}
          onDone={(text) => { setOpenEntry(null); setMsg({ tone: 'ok', text }); qc.invalidateQueries({ queryKey: ['agent', 'milk'] }); }} />
      )}
      {scanning && <QrScanner title={mode === 'single' ? 'Scan the cow' : 'Scan each cow'} hint="Point the camera at the cow's QR code or ear-tag barcode." onClose={() => setScanning(false)} onResult={scanned} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Milk Delivery - where the milk went, the same choices as the app's Milk
// Sale: the Dairy Hub (slip photo), a Partner / Customer / Vendor (scan
// their QR, or record without QR for the office to check), or used on the
// farm (calves / office, no amount).
// ---------------------------------------------------------------------------

type SaleChoice = 'Hub' | 'Partner' | 'Customer' | 'Vendor' | 'Calves' | 'Office';
const SALE_CHOICES: { id: SaleChoice; word: WordKey }[] = [
  { id: 'Hub', word: 'dairyHub' },
  { id: 'Partner', word: 'partner' },
  { id: 'Customer', word: 'customer' },
  { id: 'Vendor', word: 'vendor' },
  { id: 'Calves', word: 'calves' },
  { id: 'Office', word: 'office' },
];

function MilkDeliveryTab({ day, entryDay, onToday, refresh, choice, setChoice }: {
  day: AgentDetail; entryDay: string; onToday: () => void; refresh: () => void; choice: SaleChoice | null; setChoice: (c: SaleChoice) => void;
}) {
  const { t } = useLang();
  return (
    <div className="flex flex-col gap-3">
      <MissedBanner day={entryDay} onToday={onToday} />
      <section className="flex flex-col gap-2 rounded-xl bg-white p-4">
        <p className="font-bold">{t('milkDelivery')}</p>
        <p className="text-xs text-ink-500">{t('whereMilk')}</p>
        <div className="grid grid-cols-3 gap-2">
          {SALE_CHOICES.map((c) => (
            <button key={c.id} type="button" onClick={() => setChoice(c.id)}
              className={cn('flex flex-col items-center rounded-xl border px-1 py-2.5 text-center text-sm font-bold leading-tight', choice === c.id ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/50 text-ink-700')}>
              {t(c.word)}
            </button>
          ))}
        </div>
      </section>
      {choice === 'Hub' ? <HubTab day={day} date={entryDay} refresh={refresh} />
        : choice ? <PartySale key={`${choice}-${entryDay}`} choice={choice} day={entryDay} refresh={refresh} />
          : <Notice tone="ok">{t('whereMilk')}</Notice>}
    </div>
  );
}

function PartySale({ choice, day, refresh }: { choice: Exclude<SaleChoice, 'Hub'>; day: string; refresh: () => void }) {
  const { t } = useLang();
  const consumption = choice === 'Calves' || choice === 'Office';
  const parties = useQuery({ queryKey: ['agent', 'parties'], queryFn: agentApi.parties, staleTime: 120_000, enabled: !consumption });
  const auto = useFarmShift();
  const [manualShift, setManualShift] = useState<Shift | null>(null);
  const shift = manualShift ?? auto;
  const isToday = day === todayIso();
  const [partyId, setPartyId] = useState('');
  const [search, setSearch] = useState('');
  const [litres, setLitres] = useState('');
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');
  const [scan, setScan] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // This card's own type first; the others stay findable, marked with their type.
  const list = useMemo(() => [...(parties.data ?? [])]
    .sort((a, b) => Number(b.party_type === choice) - Number(a.party_type === choice) || a.name.localeCompare(b.name)), [parties.data, choice]);
  const term = search.trim().toLowerCase();
  const shown = (term ? list.filter((p) => p.name.toLowerCase().includes(term)) : list.filter((p) => p.party_type === choice)).slice(0, 50);
  const party = list.find((p) => p.id === partyId);
  const planLitres = (p: typeof party, s: Shift) => {
    const v = p?.subscription_active ? (s === 'Morning' ? p.subscription_morning_litres : p.subscription_evening_litres) : null;
    return v != null ? Number(v) : null;
  };
  // A subscribed buyer's plan fills in this shift's litres and the rate; still editable.
  const applyPlan = (p: typeof party, s: Shift) => {
    if (!p) return;
    const l = planLitres(p, s);
    if (l != null) setLitres(String(l));
    const r = p.subscription_active && p.subscription_rate != null ? p.subscription_rate : p.default_rate_per_litre;
    if (r != null) setRate(String(Number(r)));
  };
  const pickParty = (id: string) => { setPartyId(id); setMsg(null); applyPlan(list.find((p) => p.id === id), shift); };
  const pickShift = (s: Shift | null) => { setManualShift(s); applyPlan(party, s ?? auto); };
  // The plan's litres follow the shift when it changes by itself at 12:00.
  useEffect(() => { if (!manualShift) applyPlan(party, auto); }, [auto]); // eslint-disable-line react-hooks/exhaustive-deps
  const valid = Number(litres) > 0 && (consumption || (!!partyId && rate !== '' && Number(rate) >= 0));
  const done = (text: string, tone: 'ok' | 'warn' = 'ok') => {
    setMsg({ tone, text });
    setLitres(''); setNotes(''); setPartyId(''); setSearch('');
    refresh();
  };

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setMsg(null);
    try { await work(); } catch (err) { setMsg({ tone: 'bad', text: getApiErrorMessage(err, 'Could not save. Please try again.') }); } finally { setBusy(false); }
  };
  // Scanned: verified by the server at once - AM/PM comes from the clock.
  const scanned = (token: string) => {
    setScan(false);
    run(async () => {
      const d = await agentApi.confirm({
        customer_id: partyId, sale_date: todayIso(), verification_mode: 'QRScan', qr_token: token.trim(),
        litres: Number(litres), rate_per_litre: Number(rate),
      });
      done(`✓ ${party?.name}: ${Number(litres)} L delivered (QR checked).${d.account_warning ? ` ${d.account_warning}` : ''}`, d.account_warning ? 'warn' : 'ok');
    });
  };
  const record = () => run(async () => {
    const category = consumption ? (choice === 'Calves' ? 'CalfConsumption' : 'OfficeConsumption') : party?.party_type === 'Partner' ? 'Partner' : 'Customer';
    await agentApi.sale({
      sale_category: category, sale_date: day, shift, litres: Number(litres),
      rate_per_litre: consumption ? 0 : Number(rate), notes: notes || undefined,
      ...(consumption ? {} : { customer_id: partyId }),
    });
    done(consumption ? `✓ ${Number(litres)} L for ${choice === 'Calves' ? 'calves' : 'office use'} saved.`
      : `⏳ ${party?.name}: ${Number(litres)} L saved without QR - the office checks it before it counts.`, consumption ? 'ok' : 'warn');
  });

  return (
    <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
      <ShiftPicker options={[{ id: 'Morning' as Shift, label: t('am') }, { id: 'Evening' as Shift, label: t('pm') }]}
        value={shift} auto={auto} manual={manualShift !== null} onPick={pickShift} />

      {!consumption && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">{t(SALE_CHOICES.find((c) => c.id === choice)!.word)}</p>
          {party ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-brand-green-600 bg-brand-green-50 px-3 py-2.5">
              <span className="min-w-0">
                <span className="block truncate font-semibold">{party.name}</span>
                {party.subscription_active && (
                  <span className="block text-xs text-ink-600">
                    Plan: {[party.subscription_morning_litres != null && `AM ${Number(party.subscription_morning_litres)} L`, party.subscription_evening_litres != null && `PM ${Number(party.subscription_evening_litres)} L`].filter(Boolean).join(' + ')}
                    {party.subscription_rate != null ? ` @ ₹${Number(party.subscription_rate)}/L` : ''}
                    {planLitres(party, shift) == null ? ' · no delivery in this shift on the plan' : ''}
                  </span>
                )}
              </span>
              <button type="button" onClick={() => setPartyId('')} className="shrink-0 text-xs font-bold text-brand-green-800 underline">Change</button>
            </div>
          ) : (
            <>
              <input className={inputClass} placeholder="Search name…" value={search} onChange={(e) => setSearch(e.target.value)} />
              {parties.isError && <Notice tone="bad">{getApiErrorMessage(parties.error, 'Could not load the list.')}</Notice>}
              <ul className="flex max-h-60 flex-col divide-y divide-ink-300/20 overflow-y-auto rounded-lg border border-ink-300/30">
                {shown.map((p) => (
                  <li key={p.id}>
                    <button type="button" onClick={() => pickParty(p.id)} className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm">
                      <span className="truncate font-semibold">{p.name}</span>
                      <span className="shrink-0 text-xs text-ink-500">{p.party_type !== choice ? p.party_type : p.subscription_active ? 'subscription' : ''}</span>
                    </button>
                  </li>
                ))}
                {parties.isSuccess && !shown.length && <li className="px-3 py-3 text-sm text-ink-500">None found{term ? '' : ` - search to find other types`}.</li>}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <label className="text-sm font-semibold">{t('litres', { short: true })} *<input className={inputClass} inputMode="decimal" value={litres} onChange={(e) => setLitres(e.target.value)} /></label>
        {!consumption && <label className="text-sm font-semibold">{t('rate', { short: true })} *<input className={inputClass} inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} /></label>}
      </div>
      {!consumption && Number(litres) > 0 && rate !== '' && <p className="text-right text-lg font-bold">{formatCurrency(Number(litres) * Number(rate))}</p>}
      {consumption && <p className="text-xs text-ink-500">Used on the farm - no amount is charged.</p>}
      <label className="text-sm font-semibold">{t('notes', { short: true })}<input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}

      {consumption ? (
        <button type="button" disabled={!valid || busy} onClick={record} className={cn(bigBtn, 'bg-brand-green-700 text-base text-white')}>
          {busy && <Loader2 className="h-5 w-5 animate-spin" />} {t('save')}
        </button>
      ) : (
        <>
          {/* Preferred: the buyer shows their QR - checked and counted at once. A QR only proves today. */}
          {isToday && (
            <button type="button" disabled={!valid || busy} onClick={() => setScan(true)} className={cn(bigBtn, 'bg-brand-green-700 text-base text-white')}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <QrCode className="h-5 w-5" />} {t('scanCustomerQr')}
            </button>
          )}
          <button type="button" disabled={!valid || busy} onClick={record}
            className={cn(bigBtn, isToday ? 'border border-brand-green-700 text-brand-green-800' : 'bg-brand-green-700 text-base text-white')}>
            {t('recordWithoutQr')}
          </button>
          <p className="text-center text-xs text-ink-500">
            {isToday ? 'With QR the shift is set by the time now. Without QR, the office checks it before it counts.'
              : 'A customer\'s QR only works on the day itself - a missed day is recorded without QR.'}
          </p>
        </>
      )}
      {scan && party && <QrScanner title={`${party.name} · ${Number(litres)} L`} hint="Point the camera at the customer's delivery QR." onClose={() => setScan(false)} onResult={scanned} />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Dairy Hub
// ---------------------------------------------------------------------------

function HubTab({ day, date, refresh }: { day: AgentDetail; date: string; refresh: () => void }) {
  const { t } = useLang();
  const rule = useFormRule('milk-sales');
  const def = (name: string, fallback: string) => {
    const v = rule.fields?.[name]?.default;
    return v != null && v !== '' ? String(v) : fallback;
  };
  const hubs = useQuery({ queryKey: ['agent', 'hubs'], queryFn: agentApi.hubs, staleTime: 300_000 });
  const status = day.hub ?? [];
  const isToday = date === todayIso();
  // Today: only the shifts still to record. A missed earlier day: either
  // shift (the server refuses a shift that already has a Hub entry).
  const open: Shift[] = isToday ? status.filter((h) => h.started && h.sale_count === 0).map((h) => h.shift) : ['Morning', 'Evening'];
  // The shift on now by the clock (if it's still to record), else the one
  // still open; the agent can pick the other one.
  const auto = useFarmShift();
  const [manualShift, setShift] = useState<Shift | ''>('');
  const shift: Shift | '' = manualShift && open.includes(manualShift) ? manualShift
    : (open.includes(auto) ? auto : open[0]) ?? '';
  const [hubId, setHubId] = useState('');
  const [litres, setLitres] = useState('');
  const [fat, setFat] = useState('');
  const [rate, setRate] = useState('');
  const [member, setMember] = useState('');
  const [person, setPerson] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => { setShift(''); setSaved(null); }, [date]);

  useEffect(() => { if (!hubId && hubs.data?.length) setHubId((hubs.data.find((h) => h.is_default) ?? hubs.data[0]).id); }, [hubs.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setMember((m) => m || def('member_number', '0160')); setPerson((p) => p || def('person_name', 'Shoalin')); }, [rule]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => agentApi.hubSale({
      sale_category: 'DairyCollectionHub', sale_date: date, shift, dairy_collection_hub_id: hubId,
      member_number: member || undefined, person_name: person || undefined,
      litres: Number(litres), fat_percent: Number(fat), rate_per_litre: Number(rate), notes: notes || undefined,
    }, photo!),
    onSuccess: () => {
      setSaved(`✓ ${Number(litres)} L to the Hub (${isToday ? '' : `${formatDate(date)} `}${shift === 'Morning' ? 'AM' : 'PM'}) saved - it counts once the office approves it.`);
      setLitres(''); setFat(''); setNotes(''); setPhoto(null); setShift('');
      refresh();
    },
  });
  // Fat % is a must for the Hub - it sets the Hub's price.
  const fatOk = fat !== '' && Number(fat) > 0 && Number(fat) <= 15;
  const ready = !!shift && !!hubId && Number(litres) > 0 && fatOk && Number(rate) >= 0 && rate !== '' && !!photo;

  return (
    <div className="flex flex-col gap-3">
      <section className="rounded-xl bg-white p-4">
        <p className="mb-2 flex items-center gap-2 font-bold"><Warehouse className="h-5 w-5 text-info-600" /> {t('hubToday')}</p>
        <ul className="flex flex-col gap-2">
          {status.map((h) => (
            <li key={h.shift} className="flex items-center gap-3">
              <ShiftBadge shift={h.shift} />
              {h.sale_count > 0 ? (
                <span className="flex items-center gap-1 text-sm font-semibold text-success-700"><Check className="h-4 w-4" /> {Number(h.litres)} L{h.pending_approval ? ' · waiting for approval' : ''}</span>
              ) : !h.started ? <span className="text-sm text-ink-400">From 12:00</span> : <span className="text-sm font-semibold text-warning-700">{t('stillToRecord')}</span>}
            </li>
          ))}
        </ul>
      </section>
      {saved && <Notice tone="ok">{saved}</Notice>}
      {open.length > 0 ? (
        <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
          <p className="font-bold">{t('recordHub')}{isToday ? '' : ` - ${formatDate(date)}`}</p>
          <div className="grid grid-cols-2 gap-2">
            {open.map((s) => (
              <button key={s} type="button" onClick={() => setShift(s === auto ? '' : s)}
                className={cn('rounded-xl border py-2.5 text-sm font-bold', shift === s ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/50')}>
                {s === 'Morning' ? t('am') : t('pm')}
              </button>
            ))}
          </div>
          {(hubs.data?.length ?? 0) > 1 && (
            <label className="text-sm font-semibold">Hub
              <select className={inputClass} value={hubId} onChange={(e) => setHubId(e.target.value)}>
                {hubs.data!.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm font-semibold">{t('litres', { short: true })} *<input className={inputClass} inputMode="decimal" value={litres} onChange={(e) => setLitres(e.target.value)} /></label>
            <label className="text-sm font-semibold">{t('fat', { short: true })} *<input className={cn(inputClass, fat !== '' && !fatOk && 'border-critical-500')} inputMode="decimal" value={fat} onChange={(e) => setFat(e.target.value)} />
              {fat !== '' && !fatOk && <span className="text-xs font-medium text-critical-600">Enter the fat % from the slip (e.g. 3.5)</span>}
            </label>
            <label className="text-sm font-semibold">{t('rate', { short: true })} *<input className={inputClass} inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} /></label>
            <label className="text-sm font-semibold">Member no.<input className={inputClass} value={member} onChange={(e) => setMember(e.target.value)} /></label>
          </div>
          <label className="text-sm font-semibold">Operator at the Hub<input className={inputClass} value={person} onChange={(e) => setPerson(e.target.value)} /></label>
          <label className="text-sm font-semibold">{t('notes', { short: true })}<input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
          <label className={cn('flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-bold', photo ? 'border-success-600 text-success-700' : 'border-ink-300 text-ink-600')}>
            <Camera className="h-5 w-5" /> {photo ? 'Slip photo added ✓ (tap to retake)' : `${t('slipPhoto')} *`}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
          </label>
          {save.isError && <Notice tone="bad">{getApiErrorMessage(save.error, 'Could not save. Please try again.')}</Notice>}
          <button type="button" disabled={!ready || save.isPending} onClick={() => save.mutate()} className={cn(bigBtn, 'bg-brand-green-700 text-base text-white')}>
            {save.isPending && <Loader2 className="h-5 w-5 animate-spin" />} {t('save')}
          </button>
          {!ready && (
            <p className="text-center text-xs text-ink-500">
              Still needed: {[!(Number(litres) > 0) && 'litres', !fatOk && 'fat %', rate === '' && 'rate', !photo && 'slip photo'].filter(Boolean).join(', ')}
            </p>
          )}
        </section>
      ) : (
        <Notice tone="ok">Nothing to record for the Hub right now.</Notice>
      )}
      {!!day.sales_recent?.length && (
        <section className="rounded-xl bg-white p-4">
          <p className="mb-2 text-sm font-bold">My recent entries</p>
          <ul className="flex flex-col gap-1 text-sm">
            {day.sales_recent.map((s) => (
              <li key={s.id} className="flex justify-between gap-2">
                <span className="truncate">{s.party_name} {s.shift ? `· ${s.shift === 'Morning' ? 'AM' : 'PM'}` : ''} · {Number(s.litres)} L</span>
                <span className={cn('shrink-0 font-semibold', s.status === 'Approved' ? 'text-success-700' : s.status === 'Rejected' ? 'text-critical-700' : 'text-warning-700')}>{s.status}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

function TasksTab() {
  const qc = useQueryClient();
  const { t: tr } = useLang();
  const [showDone, setShowDone] = useState(false);
  const [reopenFor, setReopenFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const tasks = useQuery({ queryKey: ['agent', 'tasks', showDone], queryFn: () => agentApi.tasks(showDone), refetchInterval: LIVE_MS });
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agent'] }); setReopenFor(null); setReason(''); },
  });
  const list = tasks.data ?? [];
  const isJob = (t: MyTask) => t.kind === 'occurrence' && !!t.occurrence_id;
  const id = (t: MyTask) => t.occurrence_id ?? t.assignment_id;
  const open = (t: MyTask) => !t.completed_at && !t.locked && !t.later;
  const groups: [string, MyTask[]][] = [
    [tr('am'), list.filter((t) => open(t) && t.shift === 'Morning')],
    [tr('pm'), list.filter((t) => open(t) && t.shift === 'Evening')],
    ['Any time', list.filter((t) => open(t) && t.shift !== 'Morning' && t.shift !== 'Evening')],
    ['Later today', list.filter((t) => !t.completed_at && t.later)],
    ['Locked - missed by 24 hours', list.filter((t) => !t.completed_at && t.locked)],
    [tr('markDone'), list.filter((t) => !!t.completed_at)],
  ];

  const card = (t: MyTask) => {
    const need = t.proof_photos ?? 0;
    const have = t.photo_count ?? 0;
    const busy = act.isPending;
    return (
      <li key={id(t)} className={cn('rounded-xl border bg-white p-3', t.completed_at ? 'border-success-100' : t.locked ? 'border-critical-500/40' : 'border-ink-300/30')}>
        <p className="flex items-center gap-2 font-bold">
          {t.completed_at ? <CheckCircle2 className="h-5 w-5 text-success-600" /> : t.locked ? <Lock className="h-5 w-5 text-critical-600" /> : null}
          {t.title}
        </p>
        <p className="text-xs text-ink-500">
          {t.task_type} · {t.priority}{t.due_date && !isJob(t) ? ` · due ${formatDate(t.due_date)}` : ''}{need ? ` · ${have}/${need} photos` : ''}{t.started_at && !t.completed_at ? ' · started' : ''}
        </p>
        {t.description && <p className="mt-1 text-sm text-ink-600">{t.description}</p>}
        {t.locked && !t.completed_at && isJob(t) && (
          t.reopen_requested_at ? <p className="mt-2 text-sm font-semibold text-warning-700">Reopen asked - waiting for the office</p>
            : reopenFor === id(t) ? (
              <div className="mt-2 flex gap-2">
                <input className={inputClass} placeholder="Why was it missed?" value={reason} onChange={(e) => setReason(e.target.value)} />
                <button type="button" disabled={reason.trim().length < 3 || act.isPending} onClick={() => act.mutate(() => agentApi.reopen(t, reason.trim()))}
                  className="shrink-0 rounded-lg bg-brand-green-700 px-3 text-sm font-bold text-white disabled:opacity-50">Send</button>
              </div>
            ) : <button type="button" onClick={() => { setReopenFor(id(t)); setReason(''); }} className="mt-2 text-sm font-bold text-brand-green-800 underline">Ask the office to reopen</button>
        )}
        {open(t) && (
          <div className="mt-2 flex gap-2">
            {isJob(t) ? (
              <>
                {need > have && (
                  <label className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-brand-green-700 py-2.5 text-sm font-bold text-brand-green-800">
                    <Camera className="h-4 w-4" /> Photo ({have + 1}/{need})
                    <input type="file" accept="image/*" {...(t.proof_source === 'any' ? {} : { capture: 'environment' as const })} className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) act.mutate(() => agentApi.taskPhoto(t, f)); }} />
                  </label>
                )}
                <button type="button" disabled={need > have || !!busy} onClick={() => window.confirm(`Mark "${t.title}" as done?`) && act.mutate(() => agentApi.completeTask(t))}
                  className={cn(bigBtn, 'flex-1 bg-brand-green-700 text-white')}>{tr('markDone')}</button>
              </>
            ) : !t.started_at ? (
              <button type="button" disabled={!!busy} onClick={() => act.mutate(() => agentApi.startTask(t))} className={cn(bigBtn, 'flex-1 border border-brand-green-700 text-brand-green-800')}>{tr('start')}</button>
            ) : (
              <button type="button" disabled={!!busy} onClick={() => window.confirm(`Mark "${t.title}" as done?`) && act.mutate(() => agentApi.completeTask(t))}
                className={cn(bigBtn, 'flex-1 bg-brand-green-700 text-white')}>{tr('markDone')}</button>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="flex items-center gap-2 self-end text-sm text-ink-600">
        <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> {tr('showDone')}
      </label>
      {act.isError && <Notice tone="bad">{getApiErrorMessage(act.error, 'Could not update. Please try again.')}</Notice>}
      {tasks.isError && <Notice tone="bad">{getApiErrorMessage(tasks.error, 'Could not load your tasks.')}</Notice>}
      {groups.filter(([, l]) => l.length).map(([title, l]) => (
        <section key={title} className="flex flex-col gap-2">
          <h2 className="text-xs font-bold uppercase tracking-wide text-ink-600">{title} ({l.length})</h2>
          <ul className="flex flex-col gap-2">{l.map(card)}</ul>
        </section>
      ))}
      {tasks.isSuccess && !list.length && <Notice tone="ok">No tasks for you right now.</Notice>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spillage
// ---------------------------------------------------------------------------

function SpillTab({ username }: { username: string }) {
  const qc = useQueryClient();
  const { t } = useLang();
  const [litres, setLitres] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [done, setDone] = useState(false);
  const spills = useQuery({ queryKey: ['agent', 'spills'], queryFn: agentApi.spills });
  const save = useMutation({
    mutationFn: async () => {
      const pos = await getPosition();
      return agentApi.spill({ spill_date: todayIso(), litres: Number(litres), notes: notes || undefined, latitude: pos?.coords.latitude, longitude: pos?.coords.longitude }, photo!);
    },
    onSuccess: () => { setDone(true); setLitres(''); setNotes(''); setPhoto(null); qc.invalidateQueries({ queryKey: ['agent', 'spills'] }); },
  });
  const mine = (spills.data ?? []).filter((s) => s.agent_name === username).slice(0, 10);
  return (
    <div className="flex flex-col gap-3">
      <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
        <p className="font-bold">{t('reportSpill')}</p>
        <p className="text-xs text-ink-500">A photo is needed. Your location is added if allowed. The office checks it.</p>
        <label className="text-sm font-semibold">Litres spilt *<input className={inputClass} inputMode="decimal" value={litres} onChange={(e) => { setDone(false); setLitres(e.target.value); }} /></label>
        <label className="text-sm font-semibold">What happened<input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        <label className={cn('flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-bold', photo ? 'border-success-600 text-success-700' : 'border-ink-300 text-ink-600')}>
          <Camera className="h-5 w-5" /> {photo ? 'Photo added ✓' : 'Take a photo *'}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
        </label>
        {save.isError && <Notice tone="bad">{getApiErrorMessage(save.error, 'Could not save.')}</Notice>}
        {done && <Notice tone="ok">✓ Spillage reported.</Notice>}
        <button type="button" disabled={!(Number(litres) > 0) || !photo || save.isPending} onClick={() => save.mutate()} className={cn(bigBtn, 'bg-brand-green-700 text-base text-white')}>
          {save.isPending && <Loader2 className="h-5 w-5 animate-spin" />} {t('report')}
        </button>
      </section>
      {!!mine.length && (
        <section className="rounded-xl bg-white p-4">
          <p className="mb-2 text-sm font-bold">My reports</p>
          <ul className="flex flex-col gap-1 text-sm">
            {mine.map((s) => (
              <li key={s.id} className="flex justify-between"><span>{formatDate(s.spill_date)} · {Number(s.litres)} L</span><span className="font-semibold">{s.review_status}</span></li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function HistoryTab({ userId }: { userId: string }) {
  const history = useQuery({ queryKey: ['agent', 'history', userId], queryFn: () => agentApi.history(userId) });
  const byDay = useMemo(() => {
    const m = new Map<string, Delivery[]>();
    (history.data ?? []).slice(0, 200).forEach((d) => m.set(d.delivery_date, [...(m.get(d.delivery_date) ?? []), d]));
    return [...m.entries()];
  }, [history.data]);
  if (history.isError) return <Notice tone="bad">{getApiErrorMessage(history.error, 'Could not load your history.')}</Notice>;
  if (history.isLoading) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-green-700" />;
  if (!byDay.length) return <Notice tone="ok">No deliveries yet.</Notice>;
  return (
    <div className="flex flex-col gap-3">
      {byDay.map(([d, list]) => (
        <section key={d} className="flex flex-col gap-1.5">
          <h2 className="text-xs font-bold uppercase tracking-wide text-ink-600">
            {formatDate(d)} · {list.filter((x) => x.verification_mode !== 'Skipped').reduce((t, x) => t + Number(x.litres ?? 0), 0)} L
          </h2>
          <ul className="flex flex-col gap-1.5">{list.map((x) => <DoneRow key={x.id} d={x} />)}</ul>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile - who I am, language, missed / wrong entries, history, help
// ---------------------------------------------------------------------------

type MissedWhat = 'milking' | SaleChoice;
const MISSED_CHOICES: { id: MissedWhat; word: WordKey }[] = [
  { id: 'milking', word: 'milking' }, { id: 'Hub', word: 'dairyHub' }, { id: 'Partner', word: 'partner' },
  { id: 'Customer', word: 'customer' }, { id: 'Vendor', word: 'vendor' }, { id: 'Calves', word: 'calves' },
];

function ProfileRow({ icon, title, sub, onClick, href }: { icon: string; title: string; sub?: string; onClick?: () => void; href?: string }) {
  const body = (
    <>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-green-50 text-brand-green-700"><MdiIcon path={icon} className="h-5 w-5" /></span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{title}</span>
        {sub && <span className="block text-xs text-ink-500">{sub}</span>}
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-ink-300" />
    </>
  );
  const cls = 'flex w-full items-center gap-3 px-4 py-3 text-left';
  return href ? <a href={href} className={cls}>{body}</a> : <button type="button" onClick={onClick} className={cls}>{body}</button>;
}

function MyEntries({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const { t } = useLang();
  const [open, setOpen] = useState<MilkEntry | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const from = addDays(todayIso(), -7);
  const entries = useQuery({ queryKey: ['agent', 'milk', 'mine', from], queryFn: () => agentApi.milkBetween(from, todayIso()) });
  const herd = useQuery({ queryKey: ['agent', 'herd'], queryFn: agentApi.herd, staleTime: 300_000 });
  const cows = useMemo(() => new Map((herd.data ?? []).map((a) => [a.id, a])), [herd.data]);
  const mine = (entries.data ?? []).filter((e) => e.entered_by === userId && e.approval_status !== 'Rejected')
    .sort((a, b) => (`${a.production_date}${a.created_at}` < `${b.production_date}${b.created_at}` ? 1 : -1));
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-ink-500">My milk entries, last 7 days. Tap one to correct it (2 times within 24 hours, before the office approves) or to ask the office for a correction.</p>
      {msg && <Notice tone="ok">{msg}</Notice>}
      {entries.isError && <Notice tone="bad">{getApiErrorMessage(entries.error)}</Notice>}
      <ul className="flex flex-col divide-y divide-ink-300/20 rounded-xl bg-white">
        {mine.map((e) => (
          <li key={e.id}>
            <button type="button" onClick={() => { setMsg(null); setOpen(e); }} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm">
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{e.animal_id ? (cows.get(e.animal_id) ? cowName(cows.get(e.animal_id)!) : 'Cow') : `Batch of ${e.group_animal_ids?.length ?? 0}`}</span>
                <span className="block text-xs text-ink-500">{formatDate(e.production_date)} · {e.session_type === 'Morning' ? t('am', { short: true }) : e.session_type === 'Evening' ? t('pm', { short: true }) : '3rd'} · {Number(e.litres)} L</span>
              </span>
              <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold',
                e.correction_request ? 'bg-warning-50 text-warning-700' : e.approval_status === 'Approved' ? 'bg-success-50 text-success-700' : 'bg-info-50 text-info-700')}>
                {e.correction_request ? 'correction asked' : e.approval_status}
              </span>
            </button>
          </li>
        ))}
        {entries.isSuccess && !mine.length && <li className="px-3 py-3 text-sm text-ink-500">No entries in the last 7 days.</li>}
      </ul>
      {open && (
        <EntrySheet entry={open} userId={userId} cows={cows} onClose={() => setOpen(null)}
          onDone={(text) => { setOpen(null); setMsg(text); qc.invalidateQueries({ queryKey: ['agent', 'milk'] }); }} />
      )}
    </div>
  );
}

function ProfileTab({ day, onMissed, onChangePassword, onSignOut }: {
  day: AgentDetail; onMissed: (date: string, what: MissedWhat) => void; onChangePassword: () => void; onSignOut: () => void;
}) {
  const { user, roles } = useAuth();
  const { t, lang, setLang } = useLang();
  const site = useSite();
  const [view, setView] = useState<'main' | 'missed' | 'entries' | 'history' | 'help' | 'language'>('main');
  const [missedDay, setMissedDay] = useState(addDays(todayIso(), -1));
  const [missedWhat, setMissedWhat] = useState<MissedWhat>('milking');
  if (!user) return null;
  const a = day.agent;
  const backBar = (title: string) => (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => setView('main')} className="shrink-0 whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-sm font-bold text-brand-green-800">← {t('back', { short: true })}</button>
      <p className="font-bold">{title}</p>
    </div>
  );

  if (view === 'missed') return (
    <div className="flex flex-col gap-3">
      {backBar(t('recordMissed'))}
      <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
        <p className="text-sm font-bold">{t('whichDay')}</p>
        <DayPicker value={missedDay} onChange={setMissedDay} />
        <p className="text-sm font-bold">{t('whatMissed')}</p>
        <div className="grid grid-cols-3 gap-2">
          {MISSED_CHOICES.map((c) => (
            <button key={c.id} type="button" onClick={() => setMissedWhat(c.id)}
              className={cn('rounded-xl border px-1 py-2.5 text-center text-sm font-bold leading-tight', missedWhat === c.id ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/50 text-ink-700')}>
              {t(c.word)}
            </button>
          ))}
        </div>
        <Notice tone="warn">Missed entries are recorded without QR and wait for the office to check them. Customer deliveries you missed are on the Deliveries tab under "Earlier - not done" (No QR / Skip).</Notice>
        <button type="button" onClick={() => onMissed(missedDay, missedWhat)} className={cn(bigBtn, 'bg-brand-green-700 text-base text-white')}>
          {t('open')} - {formatDate(missedDay)}
        </button>
      </section>
    </div>
  );

  if (view === 'entries') return <div className="flex flex-col gap-3">{backBar(t('correctEntry'))}<MyEntries userId={user.id} /></div>;
  if (view === 'history') return <div className="flex flex-col gap-3">{backBar(t('myHistory'))}<HistoryTab userId={user.id} /></div>;

  if (view === 'language') return (
    <div className="flex flex-col gap-3">
      {backBar(t('language'))}
      <section className="flex flex-col gap-2 rounded-xl bg-white p-4">
        {LANGS.map((l) => (
          <button key={l.id} type="button" onClick={() => { setLang(l.id); setView('main'); }}
            className={cn('flex items-center justify-between rounded-xl border px-4 py-3 text-left', lang === l.id ? 'border-brand-green-700 bg-brand-green-50' : 'border-ink-300/40')}>
            <span><span className="block text-base font-bold">{l.label}</span><span className="block text-xs text-ink-500">{l.sample}</span></span>
            {lang === l.id && <Check className="h-5 w-5 text-brand-green-700" />}
          </button>
        ))}
        <p className="text-xs text-ink-500">Saved on this phone. Names, numbers and the office's messages stay as they are.</p>
      </section>
    </div>
  );

  if (view === 'help') return (
    <div className="flex flex-col gap-3">
      {backBar(t('help'))}
      <section className="flex flex-col gap-3 rounded-xl bg-white p-4 text-sm">
        {[
          ['QR or barcode will not scan', 'Tap "Take a photo of the code", or type the number printed under the barcode.'],
          ['No mobile signal / customer has no QR', 'Use "No QR" on the delivery - your GPS location is saved and the office approves it.'],
          ['Forgot to record a session', `${t('profile', { short: true })} → ${t('recordMissed', { short: true })}: pick the day and what was missed.`],
          ['Typed a wrong figure', `${t('profile', { short: true })} → ${t('correctEntry', { short: true })}: correct it yourself 2 times within 24 hours; after that, or once approved, send a correction request with the reason.`],
          ['Customer away / wants PM instead of AM', 'On the delivery, tap "Change" - pause, move AM↔PM or change the litres for the day.'],
          ['Open it like an app', 'In the browser menu tap "Add to Home screen" - the page opens from your home screen.'],
        ].map(([q, ans]) => (
          <div key={q}><p className="font-bold">{q}</p><p className="text-ink-600">{ans}</p></div>
        ))}
      </section>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <section className="flex items-center gap-3 rounded-xl bg-white p-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-green-700 text-xl font-bold text-white">{user.username.slice(0, 1).toUpperCase()}</span>
        <span className="min-w-0">
          <span className="block truncate text-lg font-bold">{user.username}</span>
          <span className="block text-xs text-ink-500">{roles.join(', ') || 'Agent'}{user.phone ? ` · ${user.phone}` : ''}</span>
          {user.email && <span className="block truncate text-xs text-ink-500">{user.email}</span>}
        </span>
      </section>
      <section className="grid grid-cols-3 gap-2 text-center">
        {[[a.assigned_customers, t('customersOnRoute', { short: true })], [a.deliveries_done_today, t('doneToday', { short: true })], [a.tasks_open, t('tasksOpen', { short: true })]].map(([n, label]) => (
          <div key={String(label)} className="rounded-xl bg-white p-3"><p className="text-2xl font-extrabold">{n}</p><p className="text-[11px] leading-tight text-ink-500">{label}</p></div>
        ))}
      </section>

      <p className="mt-1 text-xs font-bold uppercase tracking-wide text-ink-600">{t('missedOrWrong')}</p>
      <section className="flex flex-col divide-y divide-ink-300/20 rounded-xl bg-white">
        <ProfileRow icon={mdiCalendarClock} title={t('recordMissed')} sub="Milking, Dairy Hub, Partner, Customer… for an earlier day" onClick={() => setView('missed')} />
        <ProfileRow icon={mdiPencilCircleOutline} title={t('correctEntry')} sub="My milk entries of the last 7 days" onClick={() => setView('entries')} />
      </section>

      <p className="mt-1 text-xs font-bold uppercase tracking-wide text-ink-600">{t('myProfile')}</p>
      <section className="flex flex-col divide-y divide-ink-300/20 rounded-xl bg-white">
        <ProfileRow icon={mdiTranslate} title={t('language')} sub={LANGS.find((l) => l.id === lang)?.label} onClick={() => setView('language')} />
        <ProfileRow icon={mdiHistory} title={t('myHistory')} onClick={() => setView('history')} />
        <ProfileRow icon={mdiLockReset} title={t('changePassword')} onClick={onChangePassword} />
        {site.phone && <ProfileRow icon={mdiPhone} title={t('callOffice')} sub={site.phone} href={telHref(site.phone)} />}
        <ProfileRow icon={mdiHelpCircleOutline} title={t('help')} sub="Scanning, no signal, missed or wrong entries" onClick={() => setView('help')} />
        <ProfileRow icon={mdiCellphoneArrowDown} title="Add to Home screen" sub="Browser menu → Add to Home screen, to open this like an app" onClick={() => setView('help')} />
      </section>
      <button type="button" onClick={onSignOut} className={cn(bigBtn, 'border border-critical-500/50 text-critical-700')}>
        <LogOut className="h-4 w-4" /> {t('signOut')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export default function AgentPage() {
  return <LangProvider><AgentApp /></LangProvider>;
}

function AgentApp() {
  const { isAuthenticated, user, can, logout, updateUser } = useAuth();
  const { t } = useLang();
  const site = useSite();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('deliveries');
  const [saleChoice, setSaleChoice] = useState<SaleChoice | null>(null);
  // Today, or a missed earlier day (started from Profile -> Record a missed session).
  const [entryDay, setEntryDay] = useState(todayIso());
  const [changingPassword, setChangingPassword] = useState(false);
  const allowed = isAuthenticated && can('Deliveries', 'View');
  const day = useQuery({ queryKey: ['agent', 'me'], queryFn: agentApi.me, enabled: allowed, refetchInterval: LIVE_MS, refetchOnWindowFocus: true });
  useEffect(() => { window.scrollTo({ top: 0 }); }, [tab]);

  if (!isAuthenticated || !user) return <AgentLogin />;
  const backToToday = () => setEntryDay(todayIso());
  const openMissed = (date: string, what: MissedWhat) => {
    setEntryDay(date);
    if (what === 'milking') setTab('milking');
    else { setSaleChoice(what); setTab('sale'); }
  };
  const refresh = () => { qc.invalidateQueries({ queryKey: ['agent'] }); };
  const signOut = () => { logout(); qc.removeQueries({ queryKey: ['agent'] }); };
  const d = day.data;
  const badge = d ? d.pending.filter((p) => p.is_current_shift).length + (d.hub ?? []).filter((h) => h.is_current_shift && h.sale_count === 0).length : 0;

  return (
    <div className="min-h-screen bg-surface-subtle pb-20">
      <header className="sticky top-0 z-30 border-b border-brand-green-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-xl items-center gap-2 px-4">
          <img src={logoUrl(site)} alt="" className="h-9 w-9 object-contain" />
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate font-bold text-brand-green-900">{user.username}</span>
            <span className="flex items-center gap-1 text-[11px] text-ink-400">
              <span className={cn('h-2 w-2 rounded-full', day.isError ? 'bg-critical-500' : 'bg-success-500')} />
              {day.isError ? t('offline', { short: true }) : day.dataUpdatedAt ? `${t('live', { short: true })} ${time(new Date(day.dataUpdatedAt).toISOString())}` : 'Loading…'}
            </span>
          </span>
          <button type="button" onClick={signOut} className="inline-flex items-center gap-1 rounded-full border border-ink-300/40 px-3 py-1.5 text-xs font-semibold text-ink-600">
            <LogOut className="h-3.5 w-3.5" /> {t('signOut', { short: true })}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 py-4">
        {!allowed ? (
          <Notice tone="warn">This page is for delivery agents. Your login doesn't have Deliveries access - ask the office to give it, or use the main app.</Notice>
        ) : day.isError && !d ? (
          <Notice tone="bad">{getApiErrorMessage(day.error, 'Could not load your day.')}</Notice>
        ) : !d ? (
          <Loader2 className="mx-auto mt-10 h-8 w-8 animate-spin text-brand-green-700" />
        ) : tab === 'deliveries' ? <DeliveriesTab day={d} refresh={refresh} isAdmin={can('Deliveries', 'Approve')} goHub={() => { setSaleChoice('Hub'); setTab('sale'); }} />
          : tab === 'milking' ? <MilkingTab userId={user.id} day={entryDay} onToday={backToToday} />
          : tab === 'sale' ? <MilkDeliveryTab day={d} entryDay={entryDay} onToday={backToToday} refresh={refresh} choice={saleChoice} setChoice={setSaleChoice} />
            : tab === 'tasks' ? <TasksTab />
              : tab === 'spill' ? <SpillTab username={user.username} />
                : <ProfileTab day={d} onMissed={openMissed} onChangePassword={() => setChangingPassword(true)} onSignOut={signOut} />}
      </main>

      {allowed && (
        <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-brand-green-100 bg-white pb-[env(safe-area-inset-bottom)]" aria-label="Agent">
          <div className="mx-auto grid max-w-xl grid-cols-6">
            {TABS.map((tb) => (
              <button key={tb.id} type="button" onClick={() => setTab(tb.id)}
                className={cn('relative flex flex-col items-center gap-0.5 px-0.5 py-2 text-center text-[11px] font-medium leading-tight', tab === tb.id ? 'text-brand-green-700' : 'text-ink-400')}>
                <MdiIcon path={tb.icon} className="h-6 w-6" />
                {t(tb.word, { short: true })}
                {tb.id === 'deliveries' && badge > 0 && (
                  <span className="absolute right-[calc(50%-20px)] top-1 min-w-4 rounded-full bg-brand-gold-500 px-1 text-center text-[10px] font-bold leading-4 text-white">{badge}</span>
                )}
              </button>
            ))}
          </div>
        </nav>
      )}

      <ChangePasswordModal open={!!user.must_change_password || changingPassword} force={!!user.must_change_password}
        onClose={() => setChangingPassword(false)} onChanged={() => { setChangingPassword(false); updateUser({ must_change_password: false }); }} />
    </div>
  );
}
